import React, { Component, SyntheticEvent } from 'react';
import moment from 'moment';
import { Field } from 'bloomer/lib/elements/Form/Field/Field';
import { Label } from 'bloomer/lib/elements/Form/Label';
import { Control } from 'bloomer/lib/elements/Form/Control';
import { Input } from 'bloomer/lib/elements/Form/Input';
import { Select } from 'bloomer/lib/elements/Form/Select';
import { AnimalOptionsConsumer } from '../DataProviders/AnimalOptions';
import { Button } from 'bloomer/lib/elements/Button';
import api from '../util/api';
import { errorAlert } from '../util/helperFunctions';
import { Box } from 'bloomer/lib/elements/Box';
import Animal from '../Entities/Animal';
import Conditional from './Conditional';
import InfoTooptip from '../elements/InfoTooltip';
import styled from 'styled-components';
import { Content } from 'bloomer/lib/elements/Content';
import PatioInfoModal from './PatioInfoModal';
import { trackers } from '../util/analytics';

interface PropTypes {
  animal: Animal;
}

interface StateTypes {
  enrichmentType: string;
  walkerName: string;
  walkerType: 'staff' | 'volunteer' | '';
  isLoading: boolean;
  isModalOpen: boolean;
}

const CenteredControl = styled(Control)<any>`
  display: flex;
  align-items: center;
  .icon-tooltip {
    padding-right: 10px;
  }
`;

const StyledParagraph = styled.p<any>`
  font-size: 1.2rem;
  line-height: 1.2;
`;

class AnimalSignOutForm extends Component<PropTypes, StateTypes> {
  state = {
    enrichmentType: '',
    walkerName: '',
    walkerType: '' as StateTypes['walkerType'],
    isLoading: false,
    isModalOpen: false,
  };

  handleSubmit = async (e: SyntheticEvent) => {
    try {
      e.preventDefault();
      this.setState({
        isLoading: true,
      });
      const { animal } = this.props;
      const { walkerName, walkerType, enrichmentType } = this.state;
      const timeStamp = moment().format();

      let trainingPlan = 'no';
      if (animal.trainingPlan) {
        trainingPlan = this.props.animal.trainingPlan;
      }

      await api.animals.patch(animal.aNumber, {
        currentlyOut: true,
        signOutData: {
          trainingPlan,
          walkerName,
          walkerType,
          enrichmentType,
          aNumber: animal.aNumber,
          animalName: animal.name,
          animalType: animal.animalType,
          colorCode: animal.colorCode,
          isLongStay: animal.isLongStay,
          timeTakenOut: timeStamp,
          locationAtTimeOfEnrichment: animal.getLocationName(),
        },
      });
      // if successful, the component will unmount. No need to update anything else
    } catch (err) {
      errorAlert(err);
      this.setState({
        isLoading: false,
      });
    }
  };

  handleChange = (e: SyntheticEvent) => {
    const target = e.target as HTMLInputElement;
    this.setState({
      [target.name]: target.value,
    } as any);
  };

  closeModal = () => {
    this.setState({
      isModalOpen: false,
    });
  };

  openModal = () => {
    trackers.catList.click().VIEW_PATIO_RULES();
    this.setState({
      isModalOpen: true,
    });
  };

  render() {
    return (
      <AnimalOptionsConsumer>
        {({ animalOptions }) => {
          const { animal } = this.props;
          const { walkerName, walkerType, enrichmentType, isLoading } = this.state;
          let enrichmentTypes: { [key: string]: string } = {};
          if (animalOptions !== null) {
            enrichmentTypes = animalOptions[animal.animalType].enrichmentTypes;
          }
          return (
            <Box>
              <form id={`submitSignOutForm-${animal.aNumber}`} onSubmit={this.handleSubmit}>
                <Field>
                  <Label htmlFor={`walkerName-${animal.aNumber}`}>Enricher's Name</Label>
                  <Control>
                    <Input
                      id={`walkerName-${animal.aNumber}`}
                      name="walkerName"
                      type="text"
                      placeholder="Ex. John Smith"
                      required={true}
                      onChange={this.handleChange}
                      value={walkerName}
                    />
                  </Control>
                </Field>

                <Field>
                  <Label htmlFor={`walkerType-${animal.aNumber}`}>Staff or Volunteer?:</Label>
                  <Control>
                    <Select
                      id={`walkerType-${animal.aNumber}`}
                      name="walkerType"
                      required={true}
                      onChange={this.handleChange}
                      value={walkerType}
                    >
                      <option value="" />
                      <option value="volunteer">Volunteer</option>
                      <option value="staff">Staff</option>
                    </Select>
                  </Control>
                </Field>

                <Field>
                  <Label htmlFor={`enrichmentType-${animal.aNumber}`}>Enrichment Type:</Label>
                  <CenteredControl>
                    <Conditional check={/patio/gi.test(enrichmentType)}>
                      <InfoTooptip interactive zIndex={39}>
                        <Content>
                          <StyledParagraph>Please be sure you are familiar with the patio rules:</StyledParagraph>
                          <Button isColor="light" onClick={this.openModal}>
                            View Rules
                          </Button>
                        </Content>
                      </InfoTooptip>
                    </Conditional>
                    <Select
                      id={`enrichmentType-${animal.aNumber}`}
                      name="enrichmentType"
                      required={true}
                      onChange={this.handleChange}
                      value={enrichmentType}
                    >
                      <option value="" />
                      {Object.entries(enrichmentTypes).map(([key, val]) => (
                        <option key={key} value={val}>
                          {val}
                        </option>
                      ))}
                    </Select>
                  </CenteredControl>
                </Field>

                <Field>
                  <Control>
                    <Button
                      id={`submitSignOut-${animal.aNumber}`}
                      isLoading={isLoading}
                      disabled={isLoading}
                      type="submit"
                      isColor="primary"
                    >
                      Submit
                    </Button>
                  </Control>
                </Field>
              </form>
              <PatioInfoModal isOpen={this.state.isModalOpen} closeModal={this.closeModal}></PatioInfoModal>
            </Box>
          );
        }}
      </AnimalOptionsConsumer>
    );
  }
}

export default AnimalSignOutForm;
